/**
 * Retries an async operation with exponential backoff
 * 
 * @param operation - The async operation to retry
 * @param maxAttempts - Maximum number of retry attempts (default: 3)
 * @param initialDelay - Initial delay in milliseconds before first retry (default: 1000)
 * @returns Promise that resolves with the operation result or rejects with the last error
 */
export function retryWithBackoff<T>(
  operation: () => Promise<T>,
  maxAttempts: number = 3,
  initialDelay: number = 1000
): Promise<T> {
  return new Promise((resolve, reject) => {
    let attempts = 0;
    
    const attempt = async () => {
      try {
        const result = await operation();
        resolve(result);
      } catch (error) {
        attempts++;
        
        if (attempts >= maxAttempts) {
          reject(error);
          return;
        }
        
        const delay = initialDelay * Math.pow(2, attempts - 1);
        setTimeout(attempt, delay);
      }
    };
    
    attempt();
  });
}